import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  UserPlus,
  Heart,
  MessageCircle,
  Bookmark,
  Building2,
  Mail,
  Bell,
  ImageOff,
  Megaphone,
  Circle,
  Search,
} from 'lucide-react';
import { auth } from '../firebase';
import { useSubscribe } from '../utils/subscribeProvider';
import UserAvatar from '../components/UserAvatar';

const ORANGE = '#FF8C08';
const BG = '#08090B';
const CARD = '#23262C';
const SYSTEM_BLUE = '#29B6F6';
const WANT_BLUE = '#4DA3FF';
const LIKE_PINK = '#FF1493';

// Mirrors FirebaseChatService.buildConversationId — two uids joined in a
// stable, sorted order so both participants resolve to the same doc id.
function buildConversationId(uidA, uidB) {
  return [uidA, uidB].sort().join('_');
}

function timeAgo(timestamp) {
  if (!timestamp) return 'Just now';
  // Firestore Timestamp has a toDate(); already-hydrated data may be a Date or ms number.
  const dt =
    typeof timestamp?.toDate === 'function'
      ? timestamp.toDate()
      : timestamp instanceof Date
      ? timestamp
      : new Date(timestamp);

  const diffMs = Date.now() - dt.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  if (day < 30) return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

const TYPE_ICON = {
  subscribe: { Icon: UserPlus, color: ORANGE },
  like: { Icon: Heart, color: LIKE_PINK },
  comment: { Icon: MessageCircle, color: ORANGE },
  save: { Icon: Bookmark, color: '#4DA3FF' },
  new_listing: { Icon: Building2, color: '#2ECC71' },
  status: { Icon: Circle, color: ORANGE },
  want: { Icon: Search, color: WANT_BLUE },
  status_like: { Icon: Heart, color: LIKE_PINK },
  want_like: { Icon: Heart, color: LIKE_PINK },
  message: { Icon: Mail, color: '#9B59B6' },
  system: { Icon: Megaphone, color: SYSTEM_BLUE },
};

export default function NotificationItem({ notification, onDelete }) {
  const navigate = useNavigate();
  const subscribe = useSubscribe();
  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const type = notification.type?.toString() || '';
  const isSystem = type === 'system';
  const senderId = notification.senderId?.toString() || '';
  const targetId = notification.targetId?.toString() || '';
  const senderName =
    notification.senderName?.toString() || (isSystem ? 'Filatom' : 'Someone');
  const senderPhoto = notification.senderPhotoURL?.toString() || '';
  const isRead = notification.isRead === true;
  const conversationId = notification.conversationId?.toString() || '';

  // System-only fields — the full announcement, shown untruncated.
  const systemTitle = notification.title?.toString() || '';
  const systemMessage = notification.message?.toString() || '';

  // ── Navigation ────────────────────────────────────────────────────────
  const openProfile = (uid) => navigate(`/profile/${uid}`);
  const openListing = (id) => navigate(`/listing/${id}`);

  // Viewer screens are opened keyed by the poster's uid — they always
  // show that user's whole active status/want deck, not a single post.
  const openStatus = (uid) => navigate(`/status/${uid}`);
  const openWant = (uid) => navigate(`/want/${uid}`);

  const openChat = () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !senderId) return;

    const cid = conversationId || buildConversationId(currentUser.uid, senderId);

    navigate(`/chat/${cid}`, {
      state: {
        receiverId: senderId,
        currentUserId: currentUser.uid,
        title: senderName,
        avatarUrl: senderPhoto || null,
      },
    });
  };

  const handleTap = () => {
    switch (type) {
      case 'subscribe':
        if (senderId) openProfile(senderId);
        break;
      case 'like':
      case 'comment':
      case 'save':
      case 'new_listing':
        if (targetId) openListing(targetId);
        break;
      case 'status':
        // Opens the poster's whole active story deck, not just this one
        // post — same as tapping their ring in the feed row.
        if (senderId) openStatus(senderId);
        break;
      case 'want':
        if (senderId) openWant(senderId);
        break;
      case 'status_like':
        // The current (notified) user is the owner here, not the sender —
        // the sender only liked it — so open the current user's own
        // active status deck rather than the sender's.
        if (auth.currentUser?.uid) openStatus(auth.currentUser.uid);
        break;
      case 'want_like':
        // Same reasoning as status_like above.
        if (auth.currentUser?.uid) openWant(auth.currentUser.uid);
        break;
      case 'message':
        openChat();
        break;
      case 'system':
        // Fully displayed inline — nothing to navigate to.
        break;
      default:
        if (senderId) openProfile(senderId);
    }
  };

  // ── Subscribe toggle ──────────────────────────────────────────────────
  const handleSubscribeToggle = async () => {
    const user = auth.currentUser;
    if (!user || !senderId) return;

    setIsSubscribeLoading(true);
    try {
      await subscribe.toggleSubscribe(senderId);
    } catch (e) {
      toast.error('Failed to update subscription');
    } finally {
      setIsSubscribeLoading(false);
    }
  };

  // ── Message text ──────────────────────────────────────────────────────
  const buildMessage = () => {
    // System announcements are shown in full — title + body, no truncation,
    // and no "sender did X" sentence structure like the other types below.
    if (isSystem) {
      return (
        <div>
          <p
            style={{
              margin: 0,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {systemTitle || 'Announcement'}
          </p>
          {systemMessage && (
            <p
              style={{
                margin: '3px 0 0',
                color: '#fff',
                fontSize: 14,
                lineHeight: 1.4,
              }}
            >
              {systemMessage}
            </p>
          )}
        </div>
      );
    }

    let message;
    switch (type) {
      case 'subscribe':
        message = 'subscribed to you';
        break;
      case 'like': {
        const title = notification.listingTitle?.toString() || 'your listing';
        message = `liked "${title}"`;
        break;
      }
      case 'comment': {
        const comment = notification.commentText?.toString() || '';
        message = comment ? `commented: "${comment}"` : 'commented on your listing';
        break;
      }
      case 'save': {
        const title = notification.listingTitle?.toString() || 'your listing';
        message = `saved "${title}"`;
        break;
      }
      case 'new_listing': {
        const title = notification.listingTitle?.toString() || 'a new property';
        message = `posted a new listing: "${title}"`;
        break;
      }
      case 'status':
        message = 'posted a new status';
        break;
      case 'want': {
        const details = notification.wantDetails?.toString() || '';
        message = details
          ? `is looking for: "${details}"`
          : 'posted a new want';
        break;
      }
      case 'status_like':
        message = 'liked your status';
        break;
      case 'want_like':
        message = 'liked your want';
        break;
      case 'message': {
        const text = notification.messageText?.toString() || '';
        message = text ? `sent you: "${text}"` : 'sent you a message';
        break;
      }
      default:
        message = notification.message?.toString() || 'sent you a notification';
    }

    return (
      <p
        style={{
          margin: 0,
          color: '#fff',
          fontSize: 14,
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        <span style={{ fontWeight: 700 }}>{senderName} </span>
        {message}
      </p>
    );
  };

  // ── Type icon badge ───────────────────────────────────────────────────
  const renderTypeIcon = () => {
    const { Icon, color } = TYPE_ICON[type] || {
      Icon: Bell,
      color: 'rgba(255,255,255,0.54)',
    };
    return (
      <div
        style={{
          position: 'absolute',
          right: -4,
          bottom: -4,
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: `${color}33`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={12} color={color} />
      </div>
    );
  };

  // ── Avatar (system broadcasts have no real user behind them) ───────────
  const renderAvatar = () => {
    if (isSystem) {
      return (
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            backgroundColor: `${SYSTEM_BLUE}26`,
            border: `1px solid ${SYSTEM_BLUE}4D`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Megaphone size={22} color={SYSTEM_BLUE} />
        </div>
      );
    }
    return (
      <UserAvatar
        size={46}
        imageUrl={senderPhoto || undefined}
        name={senderName}
      />
    );
  };

  // ── Generic icon thumbnail (for status/want without image) ────────────
  const renderIconThumbnail = (IconCmp, color) => (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        backgroundColor: `${color}26`,
        border: `1px solid ${color}4D`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconCmp size={22} color={color} />
    </div>
  );

  // ── Thumbnail ─────────────────────────────────────────────────────────
  const renderThumbnail = () => {
    const url =
      notification.thumbnailUrl?.toString() ||
      notification.listingImageUrl?.toString() ||
      '';

    if (type === 'message') {
      return renderIconThumbnail(Mail, '#9B59B6');
    }

    // Wants have no single representative image in the notification
    // payload — always show the search icon rather than guessing.
    if (type === 'want') {
      return renderIconThumbnail(Search, WANT_BLUE);
    }

    // A status may carry a thumbnailUrl (image-type status); text-only
    // statuses fall back to the icon, same as wants.
    if (type === 'status' && !url) {
      return renderIconThumbnail(Circle, ORANGE);
    }

    // Status/want likes may carry a thumbnailUrl of the liked post; when
    // one wasn't captured (e.g. a text-only status), fall back to the
    // heart icon rather than the generic empty-state placeholder below.
    if ((type === 'status_like' || type === 'want_like') && !url) {
      return renderIconThumbnail(Heart, LIKE_PINK);
    }

    if (!url) {
      return (
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            backgroundColor: CARD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Building2 size={22} color="rgba(255,255,255,0.20)" />
        </div>
      );
    }

    return <ThumbnailImage url={url} />;
  };

  // ── Subscribe button ──────────────────────────────────────────────────
  const renderSubscribeButton = () => {
    // Read subscribe state synchronously, the same way UserItem does — no
    // "isInitialized" readiness gate to get stuck behind. If the provider
    // hasn't hydrated yet, these simply read as falsy/undefined for a beat
    // and then update once state lands, instead of showing a permanent spinner.
    const isSubscribed = subscribe?.isSubscribedTo?.(senderId);
    const shouldSubBack = subscribe?.shouldSubBack?.(senderId);

    let label;
    let bg;
    if (isSubscribed) {
      label = 'Subscribed';
      bg = CARD;
    } else if (shouldSubBack) {
      label = 'Sub Back';
      bg = ORANGE;
    } else {
      label = 'Subscribe';
      bg = ORANGE;
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!isSubscribeLoading) handleSubscribeToggle();
        }}
        style={{
          padding: '8px 14px',
          borderRadius: 20,
          border: isSubscribed ? '1px solid rgba(255,255,255,0.12)' : 'none',
          backgroundColor: bg,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 84,
        }}
      >
        {isSubscribeLoading ? (
          <span
            style={{
              width: 16,
              height: 16,
              border: '2px solid rgba(255,255,255,0.4)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'notifitem-spin 0.8s linear infinite',
            }}
          />
        ) : (
          <span
            style={{
              color: isSubscribed ? 'rgba(255,255,255,0.7)' : '#fff',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {label}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div
        onClick={handleTap}
        onContextMenu={(e) => {
          e.preventDefault();
          setShowDeleteConfirm(true);
        }}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: '12px 16px',
          backgroundColor: isRead ? 'transparent' : `${ORANGE}0A`,
          cursor: 'pointer',
        }}
      >
        {/* Avatar + type icon */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            onClick={(e) => {
              if (senderId && !isSystem) {
                e.stopPropagation();
                openProfile(senderId);
              }
            }}
          >
            {renderAvatar()}
          </div>
          {renderTypeIcon()}
          {!isRead && (
            <div
              style={{
                position: 'absolute',
                right: -2,
                top: -2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: ORANGE,
                border: `1.5px solid ${BG}`,
              }}
            />
          )}
        </div>

        <div style={{ width: 12, flexShrink: 0 }} />

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {buildMessage()}
          <div style={{ height: 4 }} />
          <span style={{ color: 'rgba(255,255,255,0.40)', fontSize: 12 }}>
            {timeAgo(notification.timestamp)}
          </span>
        </div>

        <div style={{ width: 10, flexShrink: 0 }} />

        {/* Right side: subscribe button, thumbnail, or nothing (system) */}
        {type === 'subscribe' ? (
          renderSubscribeButton()
        ) : isSystem ? null : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleTap();
            }}
          >
            {renderThumbnail()}
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: CARD,
              borderRadius: 16,
              padding: '24px 20px 16px',
              width: 300,
              maxWidth: '90vw',
            }}
          >
            <div
              style={{
                color: '#fff',
                fontSize: 17,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Delete Notification
            </div>
            <div style={{ height: 10 }} />
            <p
              style={{
                margin: 0,
                color: 'rgba(255,255,255,0.60)',
                fontSize: 14,
                lineHeight: 1.5,
                textAlign: 'center',
              }}
            >
              Remove this notification from your activity?
            </p>
            <div style={{ height: 20 }} />
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  onDelete?.();
                }}
                style={{
                  backgroundColor: 'rgba(255,82,82,0.15)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#FF5252',
                  fontWeight: 700,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes notifitem-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

// Small helper component so the broken-image fallback can use local state.
function ThumbnailImage({ url }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 12,
          backgroundColor: CARD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImageOff size={20} color="rgba(255,255,255,0.20)" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
}

export { NotificationItem };